import { CloseOutlined } from '@mui/icons-material';
import { Alert, Box, CircularProgress, IconButton, Stack } from '@mui/material';
import { createColumnHelper, RowSelectionState } from '@tanstack/react-table';
import React, { useCallback, useMemo, useState } from 'react';

import BudgetGroupAutocompleteForUser from '@/components/features/UserDetails/components/BudgetGroupAutocompleteForUser';
import NotSpecifiedRoleDialog from '@/components/shared/Dialog/NotSpecifiedRoleDialog';
import { DistributorRoleSwapModal } from '@/components/shared/DistributorRoleSwapModal';
import RoleSelect from '@/components/shared/RoleSelect';
import BaseTable from '@/components/shared/Table/BaseTable';
import useBaseTable from '@/components/shared/Table/hooks/useBaseTable';
import IndeterminateCheckbox from '@/components/shared/Table/IndeterminateCheckbox';
import TableSelectionActions from '@/components/shared/Table/TableSelectionActions';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { useBudgetRoleManagement } from '@/hooks/swapRoles/useBudgetRoleManagement';
import { useBudgetGroupByUser } from '@/hooks/useBudgetGroupByUser';
import { EnhancedBudgetGroup } from '@/hooks/useManageUserBudgetGroups';
import { useManageUserBudgetGroups } from '@/hooks/useManageUserBudgetGroups';
import { usePendingRoleSelection } from '@/hooks/usePendingRoleSelection';
import { useSnackbar } from '@/hooks/useSnackbar';
import { DEFAULT_PAGE_SIZE, useTablePaginationState } from '@/hooks/useTablePaginationState';
import type { components } from '@/shared/api/openapi';
import { normalizeRole } from '@/shared/constants/userGroupRole';
import { handleSnackbarError } from '@/shared/utils/errorHandler';

interface BudgetGroupsTabProps {
  userId: string | undefined;
  email: string | undefined;
}

type BudgetGroupResponse = components['schemas']['GetBudgetGroupByUserEndpoint_GetBudgetGroupByUserEndpointResponse'];

const columnHelper = createColumnHelper<BudgetGroupResponse>();

function BudgetGroupsTab({ userId, email }: BudgetGroupsTabProps) {
  const { organizationId } = useOrganizationId();
  const snackbar = useSnackbar();

  const [selectedGroupNames, setSelectedGroupNames] = useState<EnhancedBudgetGroup[]>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const { markPending, resolvePending, isBlocked, proceed, reset } = usePendingRoleSelection<number>();

  const { handleRoleChange, swapModalOpen, closeSwapModal, swapModalData, confirmSwap, isRoleChanging, isSwappingRole } =
    useBudgetRoleManagement();

  // Get pagination state from URL (single source of truth)
  const { pagination, apiPage, setPagination } = useTablePaginationState({
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const { budgetGroups, totalCount, isLoading, error, isRefetching } = useBudgetGroupByUser(apiPage, pagination.pageSize, userId || '');

  const budgetGroupsItems = useMemo(() => {
    return budgetGroups?.items || [];
  }, [budgetGroups?.items]);

  const {
    addGroups,
    removeGroup,
    removeGroups,
    loading: isMutating,
    removeGroupMutation,
    removeGroupsMutation,
  } = useManageUserBudgetGroups({ snackbar });

  const ensureValidPageAfterRemove = useCallback(
    (newTotalCount: number) => {
      const maxPageIndex = Math.max(0, Math.ceil(newTotalCount / pagination.pageSize) - 1);

      if (pagination.pageIndex > maxPageIndex) {
        setPagination((prev) => ({ ...prev, pageIndex: maxPageIndex }));
      }
    },
    [pagination.pageIndex, pagination.pageSize, setPagination],
  );

  const handleAddGroups = async () => {
    if (!email || selectedGroupNames.length === 0 || isMutating) {
      return;
    }

    try {
      const result = await addGroups(email, selectedGroupNames);

      if (result) {
        setRowSelection({});
        setSelectedGroupNames([]);
        markPending(selectedGroupNames.map((g) => g.id).filter((id): id is number => id !== undefined));
        snackbar.showSuccess('Budget groups added successfully');
      }
    } catch (err) {
      handleSnackbarError(err, snackbar, 'Failed to add budget groups');
    }
  };

  const handleRemoveGroup = useCallback(
    async (budgetGroupId: number, email: string, role: string) => {
      if (!budgetGroupId || !email || role == null) {
        return;
      }

      try {
        const success = await removeGroup(budgetGroupId, email, role);

        if (success) {
          const newTotal = totalCount - 1;
          ensureValidPageAfterRemove(newTotal);
          setRowSelection({});
          resolvePending([budgetGroupId]);
          snackbar.showSuccess('Budget group removed successfully');
        }
      } catch (err) {
        handleSnackbarError(err, snackbar, 'Failed to remove budget group');
      }
    },
    [removeGroup, totalCount, ensureValidPageAfterRemove, snackbar, resolvePending],
  );

  const handleRemoveSelectedGroups = useCallback(async () => {
    if (!email || isMutating) return;

    const groupsToRemove = budgetGroupsItems
      .filter((u) => u.group?.id && rowSelection[u.group.id.toString()] && u.role != null)
      .map((u) => ({
        groupId: u.group!.id!,
        role: u.role!,
      }));

    if (groupsToRemove.length === 0) return;

    try {
      const success = await removeGroups(email, groupsToRemove);

      if (success) {
        const removedCount = groupsToRemove.length;
        const newTotal = Math.max(0, totalCount - removedCount);
        setRowSelection({});
        ensureValidPageAfterRemove(newTotal);
        resolvePending(groupsToRemove.map((g) => g.groupId));
        snackbar.showSuccess('Selected groups removed successfully');
      }
    } catch (err) {
      handleSnackbarError(err, snackbar);
    }
  }, [budgetGroupsItems, email, ensureValidPageAfterRemove, isMutating, removeGroups, rowSelection, snackbar, totalCount, resolvePending]);

  // Define columns
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <Box sx={{ display: 'flex', justifyContent: 'left' }}>
            <IndeterminateCheckbox
              {...{
                checked: table.getIsAllRowsSelected(),
                indeterminate: table.getIsSomeRowsSelected(),
                onChange: table.getToggleAllRowsSelectedHandler(),
              }}
            />
          </Box>
        ),
        cell: ({ row }) => (
          <Box sx={{ display: 'flex', justifyContent: 'left' }}>
            <IndeterminateCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </Box>
        ),
        size: 5,
        minSize: 5,
        maxSize: 5,
      }),
      columnHelper.accessor('group.name', {
        header: 'Name',
        size: 30,
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('role', {
        header: 'Role',
        size: 30,
        cell: (info) => {
          const row = info.row.original;

          return (
            <RoleSelect
              value={normalizeRole(row.role)}
              onChange={(newRole) => {
                if (email && row.group?.id) {
                  handleRoleChange(email, row.group.id, row.group.name || '', newRole);
                  resolvePending([row.group.id]);
                }
              }}
              testId={`role-select-${info.row.index}`}
              disabled={
                isMutating ||
                isRoleChanging ||
                isSwappingRole ||
                isRefetching ||
                removeGroupMutation.isPending ||
                removeGroupsMutation.isPending
              }
            />
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        size: 30,
        cell: (info) => {
          const row = info.row.original;

          const isRemovingCurrentGroup =
            removeGroupMutation.variables?.groupId === row.group?.id && (isRefetching || removeGroupMutation.isPending);

          const isRemovingInBatch =
            removeGroupsMutation.variables?.groupRoles.some((budgetFromBatch) => budgetFromBatch.groupId === row?.group?.id) &&
            (isRefetching || removeGroupsMutation.isPending);

          return (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (email && row.group?.id && row.role != null) {
                    handleRemoveGroup(row.group?.id, email, row.role);
                  }
                }}
                disabled={
                  isMutating ||
                  isRoleChanging ||
                  isSwappingRole ||
                  isRemovingCurrentGroup ||
                  isRefetching ||
                  removeGroupMutation.isPending ||
                  removeGroupsMutation.isPending ||
                  isRemovingInBatch
                }
                data-testid={`remove-budget-groups-${info.row.id}`}
                sx={{ padding: '4px' }}
              >
                {isRemovingCurrentGroup || isRemovingInBatch ? (
                  <CircularProgress size={14} color='inherit' />
                ) : (
                  <CloseOutlined sx={{ fontSize: '16px' }} />
                )}
              </IconButton>
            </Box>
          );
        },
      }),
    ],
    [
      handleRemoveGroup,
      handleRoleChange,
      isMutating,
      isRoleChanging,
      isSwappingRole,
      removeGroupMutation,
      removeGroupsMutation,
      email,
      isRefetching,
      resolvePending,
    ],
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setRowSelection({});
    setPagination((prev) => ({ ...prev, pageIndex: newPage }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowSelection({});
    const newPageSize = parseInt(event.target.value, 10);
    setPagination({ pageIndex: 0, pageSize: newPageSize });
  };

  const tableWithData = useBaseTable<BudgetGroupResponse>({
    tableData: budgetGroupsItems,
    columns,
    isRowSelectionEnabled: true,
    isColumnVisibilityEnabled: false,
    isColumnOrderEnabled: false,
    hasPagination: true,
    rowSelection,
    onRowSelectionChange: setRowSelection,
    pagination,
    onPaginationChange: setPagination,
    paginationCount: totalCount,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: [10, 50, 100],
    getRowId: (row, index) => row.group?.id?.toString() ?? `row-${index}`,
  });

  if (error) {
    return (
      <Alert
        severity='error'
        sx={{ display: 'flex', alignItems: 'center', fontSize: '14px', lineHeight: '20px' }}
        data-testid='error-loading-budgets'
      >
        Error loading budgets: {error.message}
      </Alert>
    );
  }

  const selectedCount = Object.keys(rowSelection).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Stack
        direction='row'
        spacing={1}
        sx={{
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap-reverse',
        }}
      >
        <TableSelectionActions
          selectedCount={selectedCount}
          totalCount={budgetGroupsItems.length}
          onRemove={handleRemoveSelectedGroups}
          selectionLabel='Selected Groups'
          removeButtonText='Remove Groups'
          testIdPrefix='selected-budget-groups'
        />

        <BudgetGroupAutocompleteForUser
          userId={userId || ''}
          organizationId={organizationId}
          value={selectedGroupNames}
          onChange={(_, newValue) => setSelectedGroupNames(newValue)}
          onApply={handleAddGroups}
          disabled={isMutating}
        />
      </Stack>

      <BaseTable<BudgetGroupResponse>
        {...tableWithData}
        testId='budget-groups-table'
        ariaLabel='Budget Groups list'
        noDataMessage='No budget groups assigned.'
        loadingData={{
          isLoading: isLoading,
          loadingMessage: 'Loading...',
        }}
      />
      <DistributorRoleSwapModal
        open={swapModalOpen}
        onClose={closeSwapModal}
        onConfirm={confirmSwap}
        swapModalData={swapModalData}
        loading={isSwappingRole || isRoleChanging}
      />
      <NotSpecifiedRoleDialog open={isBlocked} onConfirm={proceed} onCancel={reset} />
    </Box>
  );
}

export default BudgetGroupsTab;
